import { useEffect, useState } from 'react'

// ============================================================================
// IS THE SOFT KEYBOARD UP? — so the tab bar can step out of its way.
//
// Owner, on the S24 (2026-10-19): with the keyboard open the floating bar rode up and sat on top
// of it. The bar must stay locked to the bottom of the screen, so while the keyboard is up the bar
// slides down out of view, and it comes back when the keyboard goes.
//
// THE SIGNAL IS THE VIEWPORT'S HEIGHT. A keyboard takes a few hundred pixels off the bottom of the
// screen; nothing else a phone does takes away that much without also changing the width. Two
// shapes of the same fact are both read, because browsers and WebViews disagree about which one
// the keyboard shrinks:
//   · Chrome (since 108) shrinks only the VISUAL viewport: `innerHeight` stays, and
//     `visualViewport.height` drops.
//   · An Android WebView may resize the LAYOUT viewport itself, so `innerHeight` drops too and
//     the two never differ — which is exactly why the bar rode up with it.
// So the height is compared with the tallest height seen at the current width (the screen with
// no keyboard), and with `innerHeight`; more than 120px missing from either means a keyboard. A
// width change (rotation) starts the baseline over.
// ============================================================================

/** How much of the screen a keyboard has to take before it counts as one. */
export const KEYBOARD_THRESHOLD_PX = 120

export interface ViewportReading {
  /** `visualViewport.height`, or `innerHeight` where there is no VisualViewport. */
  height: number
  width: number
  innerHeight: number
}

export interface KeyboardState {
  /** The tallest viewport seen at `width` — the screen without a keyboard. */
  baseline: number
  width: number
  open: boolean
}

export const NO_KEYBOARD: KeyboardState = { baseline: 0, width: 0, open: false }

/** Fold one reading into the state. Pure, so the rule is testable without a phone. */
export function readKeyboard(prev: KeyboardState, r: ViewportReading): KeyboardState {
  const sameWidth = Math.abs(r.width - prev.width) < 1
  const baseline = sameWidth ? Math.max(prev.baseline, r.height, r.innerHeight) : Math.max(r.height, r.innerHeight)
  const open =
    baseline - r.height > KEYBOARD_THRESHOLD_PX || r.innerHeight - r.height > KEYBOARD_THRESHOLD_PX
  return { baseline, width: r.width, open }
}

/** The bits of `window` the hook reads — a fake one in tests. */
export interface ViewportHost {
  innerHeight: number
  innerWidth: number
  visualViewport?: (EventTarget & { height: number; width: number }) | null
  addEventListener(type: 'resize', fn: () => void): void
  removeEventListener(type: 'resize', fn: () => void): void
}

function reading(host: ViewportHost): ViewportReading {
  const vv = host.visualViewport
  return {
    height: vv ? vv.height : host.innerHeight,
    width: vv ? vv.width : host.innerWidth,
    innerHeight: host.innerHeight,
  }
}

export function useKeyboardOpen(host: ViewportHost | null = typeof window === 'undefined' ? null : window): boolean {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!host) return
    let state = readKeyboard(NO_KEYBOARD, reading(host))
    setOpen(state.open)
    const update = () => {
      state = readKeyboard(state, reading(host))
      setOpen(state.open)
    }
    const vv = host.visualViewport
    vv?.addEventListener('resize', update)
    host.addEventListener('resize', update)
    return () => {
      vv?.removeEventListener('resize', update)
      host.removeEventListener('resize', update)
    }
  }, [host])
  return open
}
